import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { adminApi } from '@/lib/api';

const AdminPricing = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    title: '', price: '', duration: '', description: '', features: [],
    button_text: 'Get Started', button_link: '', is_featured: false, order: 0, is_active: true
  });
  const [featuresInput, setFeaturesInput] = useState('');

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const res = await adminApi.getPricing();
      setPlans(res.data);
    } catch (error) {
      toast.error('Failed to fetch pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', price: '', duration: '', description: '', features: [],
      button_text: 'Get Started', button_link: '', is_featured: false, order: plans.length, is_active: true });
    setFeaturesInput('');
    setEditingPlan(null);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData(plan);
    setFeaturesInput(plan.features.join('\n'));
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = { ...formData, features: featuresInput.split('\n').map(f => f.trim()).filter(Boolean) };
    try {
      if (editingPlan) {
        await adminApi.updatePricing(editingPlan.id, submitData);
        toast.success('Plan updated');
      } else {
        await adminApi.createPricing(submitData);
        toast.success('Plan created');
      }
      setDialogOpen(false);
      resetForm();
      fetchPlans();
    } catch (error) {
      toast.error('Failed to save plan');
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.deletePricing(id);
      toast.success('Plan deleted');
      fetchPlans();
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-pricing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Pricing Plans</h1>
          <p className="text-muted-foreground">Manage your pricing tiers</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-plan-btn"><Plus className="w-4 h-4 mr-2" /> Add Plan</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingPlan ? 'Edit Plan' : 'New Plan'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Title *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} />
                </div>
                <div className="space-y-2"><Label>Price *</Label>
                  <Input value={formData.price} onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))} placeholder="$150 or FREE" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Duration</Label>
                  <Input value={formData.duration} onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))} placeholder="Per Session" />
                </div>
                <div className="space-y-2"><Label>Order</Label>
                  <Input type="number" value={formData.order} onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))} />
                </div>
              </div>
              <div className="space-y-2"><Label>Features (one per line)</Label>
                <textarea className="w-full border rounded-md p-2 text-sm min-h-[120px]" value={featuresInput}
                  onChange={(e) => setFeaturesInput(e.target.value)} placeholder="Feature 1&#10;Feature 2" />
              </div>
              <div className="space-y-2"><Label>Button Text</Label>
                <Input value={formData.button_text} onChange={(e) => setFormData(prev => ({ ...prev, button_text: e.target.value }))} />
              </div>
              <div className="space-y-2"><Label>Button Link</Label>
                <Input value={formData.button_link} onChange={(e) => setFormData(prev => ({ ...prev, button_link: e.target.value }))} />
              </div>
              <div className="flex gap-6">
                <div className="flex items-center gap-2"><Switch checked={formData.is_featured} onCheckedChange={(c) => setFormData(prev => ({ ...prev, is_featured: c }))} /><Label>Featured</Label></div>
                <div className="flex items-center gap-2"><Switch checked={formData.is_active} onCheckedChange={(c) => setFormData(prev => ({ ...prev, is_active: c }))} /><Label>Active</Label></div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit">Save</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {loading ? ([1,2,3].map(i => <Card key={i} className="animate-pulse"><CardContent className="p-6 h-64 bg-muted"></CardContent></Card>)) :
          plans.map((plan) => (
            <Card key={plan.id} className={plan.is_featured ? 'border-primary border-2' : ''} data-testid={`plan-${plan.id}`}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-heading font-bold">{plan.title}</h3>
                    <p className="text-2xl font-bold">{plan.price} <span className="text-sm font-normal text-muted-foreground">/{plan.duration}</span></p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(plan)}><Edit className="w-4 h-4" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete Plan</AlertDialogTitle><AlertDialogDescription>Are you sure?</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(plan.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <ul className="text-sm space-y-1">
                  {plan.features.slice(0, 5).map((f, i) => <li key={i} className="text-muted-foreground">• {f}</li>)}
                  {plan.features.length > 5 && <li className="text-muted-foreground">... +{plan.features.length - 5} more</li>}
                </ul>
              </CardContent>
            </Card>
          ))}
      </div>
    </div>
  );
};

export default AdminPricing;
